import 'leaflet/dist/leaflet.css'
import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css'
import css from './OpenStreetMaps.module.scss'
import React, { useEffect, useRef } from 'react'
import * as L from 'leaflet'

import { Grid, GridColumn, GridContainer } from 'components/Layout/Grid'
import { useGoogleMapsApiKey } from 'hooks/useEnv'

import 'leaflet-defaulticon-compatibility'

interface IProps {
  lat: number
  long: number
  mapsPlaceId: string
  locationName?: string
}

const OpenStreetMaps: React.FC<IProps> = (props) => {
  const { lat, long: lng, mapsPlaceId, locationName } = props
  const key = useGoogleMapsApiKey()

  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    // Initialize map once
    if (!mapRef.current) {
      // Create map
      mapRef.current = L.map(containerRef.current, {
        center: [lat, lng],
        zoom: 12,
        scrollWheelZoom: true,
      })

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(mapRef.current)

      // Add marker
      markerRef.current = L.marker([lat, lng]).addTo(mapRef.current)
      if (locationName) {
        markerRef.current.bindPopup(locationName)
      }

      // Some container/layouts need a resize invalidation after mounting
      setTimeout(() => {
        mapRef.current && mapRef.current.invalidateSize()
      }, 0)
    } else {
      // Update view and marker for prop changes
      mapRef.current.setView([lat, lng], mapRef.current.getZoom())
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng])
        if (locationName) {
          markerRef.current.bindPopup(locationName)
        } else {
          markerRef.current.unbindPopup()
        }
      } else {
        markerRef.current = L.marker([lat, lng]).addTo(mapRef.current)
        if (locationName) {
          markerRef.current.bindPopup(locationName)
        }
      }
    }

    // Cleanup when component unmounts
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
        markerRef.current = null
      }
    }
    // Only recreate or update when lat/lng change or the key changes
  }, [lat, lng, locationName, key])

  // If app does not have the API key we return null (as before)
  if (key === '') return null

  return (
    <GridContainer>
      <Grid>
        {/* keep the same GridColumn class so styles remain compatible */}
        <GridColumn className={css.googleMapContainer} gridColumnEnd={17}>
          <div
            // preserve mapsPlaceId as a data-attribute in case something needs it
            data-maps-place-id={mapsPlaceId}
            ref={containerRef}
            style={{ height: '100%', width: '100%', filter: 'saturate(0)' }}
            role="region"
            aria-label={locationName ? `Map of ${locationName}` : 'Map'}
          />
        </GridColumn>
      </Grid>
    </GridContainer>
  )
}

export default OpenStreetMaps
